import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useTable, useSortBy } from 'react-table';
import PageTitle from "../../../layouts/PageTitle";
import { useParams, Link, useHistory, useLocation } from "react-router-dom";
import swal from "sweetalert";
import axios from 'axios';
import moment from 'moment';
import VerificationDetailsModal from './VerificationDetailsModal';
import Select from 'react-select';
import { getApiUrl } from "../../../../utils/ApiUtils";
import { NoData } from '../../SharedComponents/NoData';
import axiosInstance from '../../../../services/AxiosInstance';
import ImageComponent from '../../SharedComponents/ImageComponent';
import PdfThumbnail from './PdfThumbnail';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAdd,
  faFileInvoiceDollar,
  faSync,
  faSpinner,
  faPlusCircle,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';

import { Button, SplitButton, Dropdown, Spinner, Alert } from 'react-bootstrap';

export const Verifications = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  const COLUMNS = [
    {
      Header: 'Bilaga', // Attachment
      accessor: 'attachment_id',
      Cell: ({ row }) => <VerificationAttachment row={row} onUploadComplete={handleAttachmentUploadComplete} />
    },
    {
      Header: 'Verifikat',
      Footer: 'Verifikat',
      accessor: row => `${row.series}${row.number}`,
      id: 'verification',
      Cell: ({ value, row }) => (
        <div className="d-flex align-items-center">
          <span style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#3b82f6'
          }}>
            {value}
          </span>
          {row.original.attachments?.length > 0 && (
            <span
              className="ms-2"
              style={{
                fontSize: '0.7rem',
                backgroundColor: '#10b981',
                color: 'white',
                padding: '0.125rem 0.375rem',
                borderRadius: '0.375rem',
                fontWeight: '500'
              }}
            >
              {row.original.attachments.length}
            </span>
          )}
        </div>
      ),
    },
    {
      Header: 'Datum',
      Footer: 'Datum',
      accessor: row => moment(row.date).format('YYYY-MM-DD'),
      id: 'date',
    },
    {
      Header: 'Titel',
      Footer: 'Titel',
      accessor: 'description',
      id: 'description',
    },
    {
      Header: 'Belopp',
      Footer: 'Belopp',
      accessor: 'debitSum',
      id: 'debitSum',
      Cell: ({ value }) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value),
    },
  ];

  const columns = useMemo(() => COLUMNS, []);

  // Fetch fiscal years when component mounts
  useEffect(() => {
    fetchFiscalYears();
  }, [companyId]);

  // Fetch verifications when selectedFiscalYear changes or lastUpdate changes
  useEffect(() => {
    if (!selectedFiscalYear) return;
    fetchVerifications();
  }, [companyId, selectedFiscalYear, lastUpdate]);

  // Check for return from other views with an update flag
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('updated') === 'true') {
      // Remove the query parameter without reloading the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Trigger a refresh
      setLastUpdate(Date.now());
    }
  }, [location]);

  // Function to fetch fiscal years
  const fetchFiscalYears = async () => {
    try {
      const result = await axiosInstance.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
      setFiscalYears(result.data.fiscalYears);
      if (result.data.fiscalYears.length > 0) {
        const latestFiscalYear = result.data.fiscalYears.sort((a, b) => b.year - a.year)[0];
        setSelectedFiscalYear(latestFiscalYear);
      }
    } catch (err) {
      console.error('Error fetching fiscal years:', err);
      setError("Det gick inte att hämta räkenskapsår. Försök igen senare.");
      setFiscalYears([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch verifications
  const fetchVerifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await axiosInstance.get(getApiUrl(`${companyId}/verifications/${selectedFiscalYear.year}`), { withCredentials: true });
      const updatedVerifications = result.data.verifications.map(verification => {
        let debitSum = 0;
        let creditSum = 0;

        verification.transactions.forEach(transaction => {
          if (transaction.transaction_type === 'debit') {
            debitSum += parseFloat(transaction.amount);
          } else if (transaction.transaction_type === 'credit') {
            creditSum += parseFloat(transaction.amount);
          }
        });

        return {
          ...verification,
          debitSum,
          creditSum,
          attachment: verification.attachments || null,
        };
      });

      setData(updatedVerifications);
    } catch (err) {
      console.error('Error fetching verifications:', err);
      setError("Det gick inte att hämta verifikationer. Försök igen senare.");
      setData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handler for manual refresh button
  const handleRefresh = () => {
    setRefreshing(true);
    setLastUpdate(Date.now());
  };

  // Handler for file upload completion - trigger refresh
  const handleAttachmentUploadComplete = useCallback(() => {
    setLastUpdate(Date.now());
  }, []);

  // Handler for verification update/deletion from modal
  const handleVerificationUpdated = useCallback(() => {
    setLastUpdate(Date.now());
  }, []);

  const handleUploadClick = (event, verificationId) => {
    event.stopPropagation(); // Prevent row click
    event.preventDefault();

    // Trigger file upload
    const fileInput = document.getElementById(`upload-input-${verificationId}`);
    if (fileInput) {
      fileInput.click();
    } else {
      console.error(`File input element not found for verificationId: ${verificationId}`);
    }
  };

  const handleFileUpload = async (event, verificationId) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const uploadFileApiUrl = getApiUrl(`${companyId}/accounting/verifications/${verificationId}/attachments`);
        const response = await axiosInstance.post(uploadFileApiUrl, formData, { withCredentials: true });

        if (response.data.success) {
          handleAttachmentUploadComplete();
          //swal("Filuppladdning lyckades", "Filen har laddats upp och kopplats till verifikatet", "success");
        } else {
          swal("Filuppladdning misslyckades", response.data.message || "Ett fel uppstod vid uppladdning av filen", "error");
        }
      } catch (error) {
        swal("Filuppladdning misslyckades", "Ett fel uppstod vid uppladdning av filen", "error");
      }
    }
  };

  const handleFiscalYearChange = (selectedOption) => {
    setSelectedFiscalYear(selectedOption.value);
  };

  const tableInstance = useTable({
    columns,
    data,
  }, useSortBy);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  const handleViewDetails = (event, row) => {
    event.preventDefault();
    setSelectedRow(row);
    setShowVerificationDetails(true);
  };

  const VerificationAttachment = ({ row, onUploadComplete }) => {
    // If this is an invoice payment verification with an invoice ID
    if (row.original.invoice?.invoice_id) {
      return (
        <PdfThumbnail
          companyId={companyId}
          invoiceId={row.original.invoice.invoice_id}
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            objectFit: 'cover'
          }}
        />
      );
    }

    // Regular attachment handling
    return (
      <>
        {row.original.attachments.length > 0 ? (
          <ImageComponent
            imageUrl={getApiUrl(`${companyId}/accounting/verifications/attachments/${row.original.attachments[0].attachment_id}/thumbnail`)}
            alt=""
            style={{
              width: '40px',
              height: '40px',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}
          />
        ) : (
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '1px dashed #dee2e6',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: '#f8f9fa'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#44814e';
              e.currentTarget.style.backgroundColor = '#f0f4f1';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#dee2e6';
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onClick={(e) => handleUploadClick(e, row.original.verification_id)}
          >
            <FontAwesomeIcon
              icon={faAdd}
              style={{
                color: '#6c757d',
                fontSize: '16px'
              }}
            />
          </div>
        )}
        <input
          type="file"
          id={`upload-input-${row.original.verification_id}`}
          style={{ display: 'none' }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { handleFileUpload(e, row.original.verification_id) }}
          accept="application/pdf,image/*"
        />
      </>
    );
  };

  return (
    <>
      <div className="col-12">
        <VerificationDetailsModal
          showVerificationDetails={showVerificationDetails}
          setShowVerificationDetails={setShowVerificationDetails}
          selectedRow={selectedRow}
          onVerificationUpdated={handleVerificationUpdated}
          onUploadAttachment={(verificationId) => {
            // Use the existing file input click handler
            const fileInput = document.getElementById(`upload-input-${verificationId}`);
            if (fileInput) {
              fileInput.click();
            }
          }}
        />

        <PageTitle motherMenu="Bokföring" activeMenu={"Verifikationer"} />

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {loading && !refreshing ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Laddar...</span>
            </Spinner>
          </div>
        ) : fiscalYears.length === 0 ? (
          <>
            <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: '10px'
            }}>
              <Link
                style={{ marginTop: "20px", alignItem: "center" }}
                to={`#`}
                onClick={() => history.push(`/${companyId}/settings`)}
                className="btn btn-primary"
              >
                Gå till Inställningar
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between gap-4 mb-5">
              <div className="d-flex align-items-center gap-4">
                <span>Räkenskapsår:</span>
                <Select
                  value={{ value: selectedFiscalYear, label: selectedFiscalYear?.year }}
                  onChange={handleFiscalYearChange}
                  options={fiscalYears.map(fy => ({ value: fy, label: fy.year }))}
                  isDisabled={refreshing}
                />
              </div>
              {/*<Button variant="primary" onClick={() => history.push(`/${companyId}/newverification`)}>
                Skapa nytt verifikat
              </Button>*/}
              <SplitButton
                id="create-verification-button"
                title={
                  <span className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
                    Skapa nytt verifikat
                  </span>
                }
                variant="primary"
                onClick={() => history.push(`/${companyId}/create-bookkeeping-entry`)}
                className="split-button-custom"
              >
                <Dropdown.Item onClick={() => history.push(`/${companyId}/create-bookkeeping-entry`)}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2 text-primary" />
                    <div>
                      <div className="fw-medium">Guidad bokföring</div>
                      <small className="text-muted">Steg-för-steg med mallar</small>
                    </div>
                  </div>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => history.push(`/${companyId}/manual-bookkeeping-entry`)}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faPenToSquare} className="me-2 text-purple" />
                    <div>
                      <div className="fw-medium">Manuell bokföring</div>
                      <small className="text-muted">Friare bokföring med egna konton</small>
                    </div>
                  </div>
                </Dropdown.Item>
              </SplitButton>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Uppdaterar verifikationer...
              </div>
            )}

            {data.length === 0 ? (
              <NoData message={`Det finns inga verifikationer för ${selectedFiscalYear?.year}.`} />
            ) : (
              <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table {...getTableProps()} className="table table-hover mb-0">
                      <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                        {headerGroups.map(headerGroup => (
                          <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                              <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                className="fw-semibold border-0 py-4 px-4"
                                style={{
                                  display: column.hidden ? 'none' : 'table-cell',
                                  cursor: column.canSort ? 'pointer' : 'default',
                                  userSelect: 'none',
                                  fontSize: '0.875rem',
                                  letterSpacing: '0.025em',
                                  color: '#1e293b',
                                  backgroundColor: 'transparent'
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                  <span>{column.render('Header')}</span>
                                  {column.canSort && (
                                    <span className="ms-2">
                                      {column.isSorted ? (
                                        column.isSortedDesc ?
                                          <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                          :
                                          <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                      ) : (
                                        <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                      )}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.map((row, index) => {
                          prepareRow(row);
                          return (
                            <tr
                              {...row.getRowProps()}
                              onClick={(event) => handleViewDetails(event, row.original)}
                              style={{
                                cursor: "pointer",
                                transition: 'all 0.2s ease',
                                borderBottom: '1px solid #f1f5f9'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              {row.cells.map(cell => (
                                <td
                                  {...cell.getCellProps()}
                                  className="py-4 px-4 align-middle border-0"
                                  style={{
                                    display: cell.column.hidden ? 'none' : 'table-cell',
                                    fontSize: '0.875rem',
                                    color: '#374151'
                                  }}
                                >
                                  {cell.render('Cell')}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Modern footer with clean design */}
                  <div style={{
                    backgroundColor: '#fff',
                    borderTop: '1px solid #f1f5f9',
                    padding: '1rem 1.5rem'
                  }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Visar {rows.length} verifikationer
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1e293b' }}>
                        Total: {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(
                          data.reduce((sum, item) => sum + (item.debitSum || 0), 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Verifications;